/**
 * A referral link a visitor arrived by (/join/<code>), kept in this browser
 * until they subscribe to that creator, for up to 30 days. Once signed in
 * they're taken to the creator's page, once (PendingReferralRedirect). Storage can be unavailable
 * (private mode): then the code is simply not remembered.
 */
const KEY = 'fincreator.referral'
const MAX_AGE_MS = 30 * 24 * 3600 * 1000

interface PendingReferral {
  code: string
  creatorId: number
  savedAt: number
  /** Already sent to the creator's page after signing in. */
  redirected?: boolean
}

const read = (): PendingReferral | null => {
  try {
    const raw = localStorage.getItem(KEY)
    const value = raw ? (JSON.parse(raw) as PendingReferral) : null
    if (!value || typeof value.code !== 'string' || typeof value.creatorId !== 'number') return null
    if (Date.now() - value.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY)
      return null
    }
    return value
  } catch {
    return null
  }
}

const write = (value: PendingReferral) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value))
  } catch {
    // Not remembered; the link still works if opened again.
  }
}

export const savePendingReferral = (code: string, creatorId: number) =>
  write({ code, creatorId, savedAt: Date.now() })

/** The remembered code, if it's for this creator. */
export const pendingReferralFor = (creatorId: number): string | undefined => {
  const value = read()
  return value && value.creatorId === creatorId ? value.code : undefined
}

export const clearPendingReferral = () => {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to clear.
  }
}

/** Where to go once signed in: the creator a link was for, once; else '/'. */
export const afterSignInPath = (): string => {
  const value = read()
  if (!value || value.redirected) return '/'
  write({ ...value, redirected: true })
  return `/creator/${value.creatorId}`
}
